import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { AVATAR_KEYS, avatarImage } from './avatarArt';
import logoUrl from '../assets/images/cleveroo-logo.png';
import ReferAFriend from './ReferAFriend';
import './AccountPages.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
function authHeaders() {
  const token = localStorage.getItem('cleveroo_token') || localStorage.getItem('accessToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const PREFS_KEY = 'cleveroo_prefs';

interface Prefs {
  emailNotifications: boolean;
  soundEffects: boolean;
  reduceMotion: boolean;
}

const DEFAULT_PREFS: Prefs = {
  emailNotifications: true,
  soundEffects: true,
  reduceMotion: false,
};

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch { /* noop */ }
  return DEFAULT_PREFS;
}

/**
 * Student settings page (route: /settings).
 * Editable display name (persisted via AuthContext.updateUser) plus a few
 * local preferences stored in localStorage, and a log-out action.
 */
const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();

  const [name, setName] = useState(user?.name || '');
  const [avatar, setAvatar] = useState<string | null>(user?.avatar || null);
  const [savedName, setSavedName] = useState(false);
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);

  const nameChanged = name.trim().length > 0 && name.trim() !== (user?.name || '');

  // Persist name / avatar to the server (PATCH /api/auth/me), then reflect locally.
  const patchProfile = async (payload: { name?: string; avatar?: string | null }) => {
    const res = await axios.patch(`${API_BASE_URL}/api/auth/me`, payload, { headers: authHeaders() });
    if (res.data?.success) updateUser(res.data.data);
  };

  const saveName = async () => {
    if (!nameChanged) return;
    try {
      await patchProfile({ name: name.trim() });
      setSavedName(true);
      window.setTimeout(() => setSavedName(false), 2000);
    } catch { /* leave the field as-is on failure */ }
  };

  const pickAvatar = async (key: string) => {
    const prev = avatar;
    setAvatar(key); // optimistic
    try { await patchProfile({ avatar: key }); }
    catch { setAvatar(prev); }
  };

  const togglePref = (key: keyof Prefs) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  const toggleRows: Array<{ key: keyof Prefs; title: string; desc: string }> = [
    { key: 'emailNotifications', title: 'Email notifications', desc: 'Streak reminders and other notifications.' },
    { key: 'soundEffects', title: 'Sound effects', desc: 'Play sounds for correct answers and rewards.' },
    { key: 'reduceMotion', title: 'Reduce motion', desc: 'Minimise animations across the app.' },
  ];

  // Weekly parent summary preference — server-backed (ROO-168).
  const [digestOn, setDigestOn] = useState(true);
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/api/students/${user.id}/weekly-digest/preference`, { headers: authHeaders() })
      .then((res) => { if (!cancelled) setDigestOn(!res.data?.data?.optOut); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  const toggleDigest = () => {
    if (!user?.id) return;
    const next = !digestOn;
    setDigestOn(next);
    axios
      .post(
        `${API_BASE_URL}/api/students/${user.id}/weekly-digest/opt-out`,
        { optOut: !next },
        { headers: authHeaders() },
      )
      .catch(() => setDigestOn(!next)); // roll back on failure
  };

  // Class leaderboard opt-in — default OFF (ROO-172 C).
  const [leaderboardOn, setLeaderboardOn] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/api/students/${user.id}/leaderboard`, { headers: authHeaders() })
      .then((res) => { if (!cancelled) setLeaderboardOn(!!res.data?.data?.optedIn); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  const toggleLeaderboard = () => {
    if (!user?.id) return;
    const next = !leaderboardOn;
    setLeaderboardOn(next);
    axios
      .post(
        `${API_BASE_URL}/api/students/${user.id}/leaderboard/opt-in`,
        { optIn: next },
        { headers: authHeaders() },
      )
      .catch(() => setLeaderboardOn(!next)); // roll back on failure
  };

  return (
    <div className="cvr-account">
      <div className="cvr-account__bar">
        <button type="button" className="cvr-account__back" onClick={() => navigate('/')}>
          ← Dashboard
        </button>
        <span className="cvr-account__brand">
          <img src={logoUrl} alt="" />
          Cleveroo
        </span>
      </div>

      <div className="cvr-account__wrap">
        <h1 className="cvr-account__title">Settings</h1>

        <div className="cvr-card">
          <h2 className="cvr-card__h">Account</h2>

          <label className="cvr-label">Profile avatar</label>
          <p className="cvr-hint" style={{ marginTop: 0 }}>Pick a friend to represent you.</p>
          <div className="cvr-avatar-picker">
            {AVATAR_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={`cvr-avatar-opt${avatar === key ? ' is-selected' : ''}`}
                onClick={() => pickAvatar(key)}
                aria-label={`Choose ${key} avatar`}
                aria-pressed={avatar === key}
                title={key}
              >
                <img src={avatarImage(key) || ''} alt="" loading="lazy" />
              </button>
            ))}
          </div>

          <label className="cvr-label" htmlFor="cvr-name">Display name</label>
          <input
            id="cvr-name"
            className="cvr-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />
          <div className="cvr-field">
            <span className="cvr-field__label">Email</span>
            <span className="cvr-field__value">{user?.email || '—'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
            <button type="button" className="cvr-btn" onClick={saveName} disabled={!nameChanged}>
              Save changes
            </button>
            {savedName && <span className="cvr-saved">Saved ✓</span>}
          </div>
        </div>

        <div className="cvr-card">
          <h2 className="cvr-card__h">Preferences</h2>
          {toggleRows.map((row) => (
            <div className="cvr-toggle-row" key={row.key}>
              <div className="cvr-toggle-row__text">
                <strong>{row.title}</strong>
                <span>{row.desc}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={prefs[row.key]}
                aria-label={row.title}
                className="cvr-switch"
                onClick={() => togglePref(row.key)}
              />
            </div>
          ))}
          {/* Weekly parent summary — server-backed opt-out (ROO-168) */}
          <div className="cvr-toggle-row">
            <div className="cvr-toggle-row__text">
              <strong>Weekly parent summary</strong>
              <span>One plain-language email each week with practice and progress.</span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={digestOn}
              aria-label="Weekly parent summary"
              className="cvr-switch"
              onClick={toggleDigest}
            />
          </div>
          {/* Class leaderboard — server-backed opt-in, default OFF (ROO-172 C) */}
          <div className="cvr-toggle-row">
            <div className="cvr-toggle-row__text">
              <strong>Class leaderboard</strong>
              <span>Show my first name and weekly practice count to classmates who also join.</span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={leaderboardOn}
              aria-label="Class leaderboard"
              className="cvr-switch"
              onClick={toggleLeaderboard}
            />
          </div>
        </div>

        <ReferAFriend />

        <div className="cvr-card">
          <h2 className="cvr-card__h">Session</h2>
          <button type="button" className="cvr-btn cvr-btn--danger" onClick={logout}>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
